import { ArticleDto } from '@core/mappers/news/dtos/ArticleDto';
import {
    transformArticlePreview,
    transformFullArticle,
} from '@core/mappers/news/transformArticle';
import {
    Article,
    ArticlePreview,
    ArticleStatus,
} from '@core/models/news/Article';
import OfferEvent from '@core/models/offer/OfferEvent';

import RestService from './_private/RestService';
import { ApiArticles } from './_private/Types';

class NewsService {
    private readonly restService: RestService = new RestService();
    private currentPage = 0;

    public async getFirstArticles(): Promise<ArticlePreview[]> {
        const articlesData = await this.getArticles(0, false);
        return articlesData.articles;
    }

    public getNextArticles(): Promise<ArticleListResult> {
        return this.getArticles(this.currentPage, true);
    }

    // active only
    private async getArticles(currentPage: number, handleCurrentPage: boolean): Promise<ArticleListResult> {
        if (currentPage === -1) {
            return { articles: [], isDone: true };
        }
        const response: ApiArticles = await this.restService.getAllArticles(currentPage);
        if (handleCurrentPage) {
            if (response.meta && response.meta.pagination.current_page < response.meta.pagination.total_pages) {
                this.currentPage = response.meta!.pagination.current_page + 1;
            } else {
                // No more pages
                this.currentPage = -1;
            }
        }
        return {
            articles: response.data.map((dto) => transformArticlePreview(dto)),
            isDone: this.currentPage === -1,
        };
    }

    public async getArticlesByMatchId(id: OfferEvent['id']): Promise<ArticlePreview[]> {
        const response: {
            data: ArticleDto[];
        } = await this.restService.getArticlesByMatchId(id);
        const articles = response.data.map((dto) => transformArticlePreview(dto));
        return articles.filter(this.isArticleActive);
    }

    // eslint-disable-next-line class-methods-use-this
    private isArticleActive(article: ArticlePreview): boolean {
        const now = new Date();
        return article.status === ArticleStatus.active
            && ((article.datePublished <= now))
            && (!article.datePublishedUntil || article.datePublishedUntil >= now);
    }

    public async getArticleBySlug(slug: string): Promise<Article | null> {
        const response: {
            data: ArticleDto[];
        } = await this.restService.getArticleBySlug(slug);

        if (!response.data[0]) {
            return null;
        }

        return transformFullArticle(response.data[0]);
    }
}

export const newsService = new NewsService();

type ArticleListResult = {
    articles: ArticlePreview[];
    isDone: boolean;
};
